import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, ImagePlus } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";
import { getCategories } from "@/lib/api";

const inputClass =
  "w-full bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all placeholder:text-white/25";

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">{label}</label>
    {children}
  </div>
);

const EMPTY = {
  name: "",
  description: "",
  image: "",
  start_date: "",
  end_date: "",
  location: "",
  location_details: "",
  photos_url: "",
  price: 299,
  capacity: 30,
  categories: [],
  status: "draft",
  stage_type: "stage_payet",
};

const AdminStageForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState({ ...EMPTY, stage_type: searchParams.get("type") || "stage_payet" });
  const [allCategories, setAllCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const fileRef = useRef(null);

  useEffect(() => {
    getCategories().then(setAllCategories);
    if (isEdit) {
      adminApi.get(`/stages/${id}`).then(({ data }) => {
        setForm({
          name: data.name, description: data.description, image: data.image,
          start_date: data.start_date, end_date: data.end_date,
          location: data.location, location_details: data.location_details,
          photos_url: data.photos_url || "",
          price: data.price, capacity: data.capacity, categories: data.categories,
          status: data.status, stage_type: data.stage_type,
        });
        setLoading(false);
      }).catch((e) => {
        toast.error(apiError(e));
        navigate(-1);
      });
    }
  }, [id]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const toggleCategory = (cat) =>
    setForm({
      ...form,
      categories: form.categories.includes(cat)
        ? form.categories.filter((c) => c !== cat)
        : [...form.categories, cat].sort(),
    });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/stages/image", fd);
      setForm((f) => ({ ...f, image: data.image_url }));
      toast.success("Affiche importée.");
    } catch (err) {
      toast.error(apiError(err, "Import de l'image impossible."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.end_date || !form.location) {
      toast.error("Nom, dates et lieu sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), capacity: Number(form.capacity) };
      if (isEdit) await adminApi.put(`/stages/${id}`, payload);
      else await adminApi.post("/stages", payload);
      toast.success(isEdit ? "Stage mis à jour." : "Stage créé.");
      navigate(form.stage_type === "stage_pro_semi_pro" ? "/admin/stages/pro" : "/admin/stages/payet");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-white/40">Chargement…</p>;

  const listPath = form.stage_type === "stage_pro_semi_pro" ? "/admin/stages/pro" : "/admin/stages/payet";

  return (
    <div data-testid="admin-stage-form" className="max-w-3xl space-y-6">
      <Link to={listPath} className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-gold uppercase tracking-[0.2em] transition-colors">
        <ArrowLeft size={13} /> Retour
      </Link>
      <h1 className="font-display uppercase text-4xl tracking-wide">
        {isEdit ? "Modifier le stage" : "Ajouter un stage"}
      </h1>

      <form onSubmit={onSubmit} data-testid="stage-form" className="space-y-6">
        <section className="bg-noir-surface border border-noir-border p-6 md:p-8 space-y-5">
          <h2 className="font-display uppercase text-xl tracking-wide text-gold">Informations générales</h2>
          <Field label="Nom du stage *" testId="sf-name">
            <input data-testid="sf-input-name" value={form.name} onChange={set("name")} className={inputClass} placeholder="Ex : Stage Payet — Nice Juillet 2027" />
          </Field>
          <Field label="Description" testId="sf-description">
            <textarea data-testid="sf-input-description" rows={3} value={form.description} onChange={set("description")} className={`${inputClass} resize-y`} />
          </Field>
          <Field label="Affiche du stage (image)" testId="sf-image">
            <div className="space-y-3">
              {form.image && (
                <img src={form.image} alt="Affiche du stage" data-testid="sf-image-preview" className="h-36 object-cover border border-white/10" />
              )}
              <input
                ref={fileRef}
                data-testid="sf-input-image-file"
                id="sf-image-file"
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={uploadImage}
                className="hidden"
              />
              <label
                htmlFor="sf-image-file"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-rouge text-white text-xs font-bold uppercase tracking-wider rounded-full hover:bg-rouge-hover transition-colors cursor-pointer"
              >
                <ImagePlus size={14} /> {form.image ? "Changer l'image" : "Choisir une image"}
              </label>
              {uploading && <p className="text-xs text-gold flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Import en cours…</p>}
              <input data-testid="sf-input-image" value={form.image} onChange={set("image")} className={inputClass} placeholder="ou collez une URL https://…" />
              <p className="text-white/35 text-xs">JPG, PNG ou WEBP — 8 Mo maximum. L'image s'affiche sur la carte et la page du stage.</p>
            </div>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Date de début *" testId="sf-start">
              <input data-testid="sf-input-start" type="date" value={form.start_date} onChange={set("start_date")} className={`${inputClass} [color-scheme:dark]`} />
            </Field>
            <Field label="Date de fin *" testId="sf-end">
              <input data-testid="sf-input-end" type="date" value={form.end_date} onChange={set("end_date")} className={`${inputClass} [color-scheme:dark]`} />
            </Field>
            <Field label="Lieu *" testId="sf-location">
              <input data-testid="sf-input-location" value={form.location} onChange={set("location")} className={inputClass} placeholder="Nice" />
            </Field>
            <Field label="Adresse / infos du lieu" testId="sf-location-details">
              <input data-testid="sf-input-location-details" value={form.location_details} onChange={set("location_details")} className={inputClass} placeholder="Adresse complète (optionnel)" />
            </Field>
            <Field label="Prix (€) *" testId="sf-price">
              <input data-testid="sf-input-price" type="number" min="0" step="1" value={form.price} onChange={set("price")} className={inputClass} />
            </Field>
            <Field label="Nombre maximum de participants *" testId="sf-capacity">
              <input data-testid="sf-input-capacity" type="number" min="0" step="1" value={form.capacity} onChange={set("capacity")} className={inputClass} />
            </Field>
          </div>
          <Field label="Lien Google Drive — photos du stage" testId="sf-photos">
            <input data-testid="sf-input-photos" value={form.photos_url} onChange={set("photos_url")} className={inputClass} placeholder="https://drive.google.com/… (optionnel, modifiable à tout moment)" />
            <p className="mt-2 text-white/35 text-xs">Ajoutez ce lien quand vous voulez : les familles verront un bouton « Photos du stage » sur la page du stage.</p>
          </Field>
          <p className="text-white/35 text-xs">Le prix est stocké en valeur numérique — il alimente directement les futures sessions Stripe. Les paiements déjà réalisés ne sont jamais modifiés.</p>
        </section>

        <section className="bg-noir-surface border border-noir-border p-6 md:p-8">
          <h2 className="font-display uppercase text-xl tracking-wide text-gold mb-5">Catégories</h2>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
            {allCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                data-testid={`sf-cat-${cat}`}
                onClick={() => toggleCategory(cat)}
                className={`py-2.5 text-xs font-bold tracking-wider border transition-colors ${
                  form.categories.includes(cat)
                    ? "bg-gold text-noir border-gold"
                    : "border-white/15 text-white/50 hover:border-gold/50 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <p className="mt-4 text-white/35 text-xs">Le formulaire public d'inscription affichera uniquement les catégories cochées.</p>
        </section>

        <section className="bg-noir-surface border border-noir-border p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Statut" testId="sf-status">
            <select data-testid="sf-select-status" value={form.status} onChange={set("status")} className={inputClass}>
              <option value="draft">Brouillon (invisible publiquement)</option>
              <option value="open">Ouvert</option>
              <option value="closed">Fermé</option>
              {form.status === "archived" && <option value="archived">Archivé</option>}
            </select>
          </Field>
          <Field label="Type de stage" testId="sf-type">
            <select data-testid="sf-select-type" value={form.stage_type} onChange={set("stage_type")} className={inputClass}>
              <option value="stage_payet">Stage Payet</option>
              <option value="stage_pro_semi_pro">Stage Pro / Semi-Pro</option>
            </select>
          </Field>
          <p className="sm:col-span-2 text-white/35 text-xs">Le statut COMPLET est calculé automatiquement lorsque la capacité est atteinte.</p>
        </section>

        <button
          type="submit"
          data-testid="sf-submit"
          disabled={saving || uploading}
          className="inline-flex items-center gap-3 px-10 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isEdit ? "Enregistrer les modifications" : "Créer le stage"}
        </button>
      </form>
    </div>
  );
};

export default AdminStageForm;
